/**
 * DraftApproveDialog — pré-écran "approuver une draft" (PR2 — 2026-05-10).
 *
 * Apparaît AVANT `<MockupTemplatePicker>` quand l'œuvre a >1 variante
 * active OU quand la draft porte des `suggested_tags`. Sinon le caller
 * saute directement au picker.
 *
 * Sections :
 *   1. Variantes — radio list (skipped si 1 seule active).
 *   2. Tags suggérés Claude — `<ChipMultiSelect>` (skipped si vide).
 *   3. Footer : bouton "Choisir les mockups" (passe la suite au caller).
 *
 * Le caller chaîne avec `<MockupTemplatePicker>` puis avec
 * `useApproveDraft` (POST /drafts/{id}/approve avec
 * `mockup_template_ids` + `work_variant_id` + `accept_suggested_tags`).
 *
 * Verrous :
 *   - `<Modal>` (pas `window.confirm`).
 *   - `<Button>` pour les actions.
 *   - Aucun hex / spacing en dur.
 */

import { useState } from 'react'

import Button from '@/components/ui/Button'
import ChipMultiSelect from '@/components/ui/ChipMultiSelect'
import Modal from '@/components/ui/Modal'
import type { WorkVariantWithCatalogResponse } from '@/types/works'

import './DraftApproveDialog.css'

interface DraftApproveDialogProps {
  isOpen: boolean
  onClose: () => void
  draftKindLabel: string
  /** Variantes actives de l'œuvre. Filtrées par le caller. */
  activeVariants: WorkVariantWithCatalogResponse[]
  /** Tags suggérés Claude — vide = section sautée. */
  suggestedTags: string[]
  /**
   * Si truthy, l'œuvre a une matrice POD active : la section "Variante à
   * publier" est cachée car la matrice est l'unité de publication (toutes
   * les dimensions × finitions partent en 1 listing matriciel). Le caller
   * passe quand même les variants pour le rendu mockup (1er pris par
   * défaut, transparent pour l'utilisatrice).
   */
  hasActivePodMatrix?: boolean
  /**
   * Confirme la sélection préparatoire. Le caller enchaîne ensuite avec
   * `<MockupTemplatePicker>` puis `useApproveDraft`.
   */
  onConfirm: (selection: { workVariantId: number; acceptedTags: string[] }) => void
}

function formatVariantLabel(variant: WorkVariantWithCatalogResponse): string {
  // Aligné sur `<VariantsPanel>` (commit 220fcdf, 2026-05-15) : label
  // commercial = label_override, sinon composition dérivée
  // (format · support · framing), sinon `printable_spec.label_cache`
  // (cas open POD où format_id est NULL — le format vit dans la spec),
  // sinon fallback "Variante #N" (très rare : print/limited sans
  // composition résolue).
  if (variant.label_override) return decorateWithPriceAndEdition(variant, variant.label_override)
  const parts: string[] = []
  // 1. format pour print/original — vit sur la variante.
  if (variant.format) {
    const f = variant.format
    parts.push(f.label_fr || `${f.width_cm} × ${f.height_cm} cm`)
  }
  if (variant.support_label_fr) parts.push(variant.support_label_fr)
  if (variant.framing_label_fr) parts.push(variant.framing_label_fr)
  let base: string
  if (parts.length > 0) {
    base = parts.join(' · ')
  } else if (variant.printable_spec?.label_cache) {
    base = variant.printable_spec.label_cache
  } else {
    base = `Variante #${variant.id}`
  }
  return decorateWithPriceAndEdition(variant, base)
}

function decorateWithPriceAndEdition(
  variant: WorkVariantWithCatalogResponse,
  base: string,
): string {
  const parts = [base]
  const editionSize = variant.edition_size
  if (editionSize !== null && editionSize !== undefined && editionSize > 0) {
    parts.push(`édition de ${editionSize}`)
  }
  // Refonte tarifs 2026-05-15 (option B) — POD open print : prix dérivé
  // au publish via `pricing_service.compute_pod_retail_price_cents`, pas
  // saisi sur la variante. Mentionner « calculé au publish » plutôt que
  // d'omettre le segment (silence = peut sembler oublié).
  const isPodOpen = variant.medium_kind === 'print' && variant.edition_kind === 'open'
  const priceCents = variant.price_cents
  if (isPodOpen) {
    parts.push('prix calculé au publish')
  } else if (priceCents > 0) {
    parts.push(`${(priceCents / 100).toFixed(0)} €`)
  }
  return parts.join(' · ')
}

export default function DraftApproveDialog({
  isOpen,
  onClose,
  draftKindLabel,
  activeVariants,
  suggestedTags,
  hasActivePodMatrix = false,
  onConfirm,
}: DraftApproveDialogProps) {
  // Pattern "default + user override" pour éviter setState-in-effect (verrou
  // react-hooks/set-state-in-effect). `userVariantId === null` ⇒
  // l'utilisatrice n'a pas encore touché → on retourne le default (1re
  // variante active). Au premier toggle, override la valeur.
  const [userVariantId, setUserVariantId] = useState<number | null>(null)
  const [userTags, setUserTags] = useState<Set<string> | null>(null)

  const defaultVariantId = activeVariants.length > 0 ? activeVariants[0].id : null
  const selectedVariantId = userVariantId ?? defaultVariantId
  const selectedTags = userTags ?? new Set(suggestedTags)

  const handleConfirm = () => {
    if (selectedVariantId === null) return
    onConfirm({
      workVariantId: selectedVariantId,
      acceptedTags: Array.from(selectedTags),
    })
  }

  const handleClose = () => {
    // Reset à la fermeture pour ne pas garder la sélection précédente
    // si la dialog est ré-ouverte sur un autre draft.
    setUserVariantId(null)
    setUserTags(null)
    onClose()
  }

  const canConfirm = selectedVariantId !== null && activeVariants.length > 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Approuver le draft ${draftKindLabel}`}
      size="form"
    >
      <Modal.Body>
        {hasActivePodMatrix ? (
          <div className="draft-approve-dialog-section">
            <p className="draft-approve-dialog-section-hint">
              Cette œuvre a une matrice POD active — toutes les dimensions et finitions partiront en
              un seul listing Etsy matriciel. Tu n&apos;as pas de variante à choisir.
            </p>
          </div>
        ) : activeVariants.length > 1 ? (
          <div className="draft-approve-dialog-section">
            <h3 className="draft-approve-dialog-section-title">Variante à publier</h3>
            <p className="draft-approve-dialog-section-hint">
              Le rendu mockup s&apos;adapte au ratio de la variante. Tu peux republier sur une autre
              variante depuis la fiche œuvre.
            </p>
            <ul className="draft-approve-dialog-variant-list">
              {activeVariants.map((variant) => {
                const isSelected = variant.id === selectedVariantId
                return (
                  <li key={variant.id}>
                    <Button
                      type="button"
                      variant="ghost"
                      role="radio"
                      aria-checked={isSelected}
                      className={
                        isSelected
                          ? 'draft-approve-dialog-variant-item draft-approve-dialog-variant-item--selected'
                          : 'draft-approve-dialog-variant-item'
                      }
                      onClick={() => setUserVariantId(variant.id)}
                    >
                      <span
                        aria-hidden="true"
                        className="draft-approve-dialog-variant-radio"
                        data-checked={isSelected ? 'true' : 'false'}
                      />
                      <span className="draft-approve-dialog-variant-label">
                        {formatVariantLabel(variant)}
                      </span>
                    </Button>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}

        {suggestedTags.length > 0 ? (
          <div className="draft-approve-dialog-section">
            <h3 className="draft-approve-dialog-section-title">Tags suggérés par Claude</h3>
            <p className="draft-approve-dialog-section-hint">
              Ces tags seront ajoutés à l&apos;œuvre. Décoche ce qui ne te convient pas.
            </p>
            <ChipMultiSelect
              options={suggestedTags.map((tag) => ({ value: tag, label: tag }))}
              value={Array.from(selectedTags)}
              onChange={(next) => setUserTags(new Set(next))}
            />
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="neutral" onClick={handleClose}>
          Annuler
        </Button>
        <Button type="button" variant="primary" onClick={handleConfirm} disabled={!canConfirm}>
          Choisir les mockups
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
